import { TLEditorSnapshot } from '@tldraw/tldraw';
import { Save, AlertTriangle, X } from 'lucide-react';
import { useQueryClient } from 'react-query';
import { useEditor, track, getSnapshot, loadSnapshot } from 'tldraw';

import { useTimelineChanges } from '../../hooks/useTimelineChanges';
import { createTimeline } from '../../timelineDrawingHelpers';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useSaveChronologyVisualisation } from '@/api/mutations/useSaveChronologyVisualisation';
import { QueryEntity } from '@/api/types';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/Tooltip';
import { ChronologyFact } from '@/types';

interface SaveButtonProps {
  caseId: string | null;
  chronologyId: string | null;
  chronology: ChronologyFact[];
  originalSnapshot: TLEditorSnapshot | undefined;
  timelineChanges: ReturnType<typeof useTimelineChanges>;
}

export const SaveButton = ({
  caseId,
  chronologyId,
  chronology,
  originalSnapshot,
  timelineChanges,
}: SaveButtonProps) => {
  const editor = useEditor();
  const queryClient = useQueryClient();
  const { mutate: saveVisualisation, isLoading: isSavingVisualisation } = useSaveChronologyVisualisation();
  const {
    changeCount,
    hasUnsavedChanges,
    isSaving: isSavingChanges,
    saveAllChanges,
    discardAllChanges,
  } = timelineChanges;

  const isSaving = isSavingVisualisation || isSavingChanges;

  const handleSave = async () => {
    if (!editor || !caseId || !chronologyId || isSaving) return;

    trackCustomEvent(TrackingEvents.SaveVisualChronology, {
      caseId: caseId,
      chronologyId: chronologyId,
    });

    // First save any pending changes to the chronology in the db
    if (hasUnsavedChanges) {
      await saveAllChanges();

      //Invalidate the chronology query so we use an up to date chronology if we are rebuilding it
      queryClient.invalidateQueries({ queryKey: [QueryEntity.ChronologyFacts, { chronologyId }, { paginated: true }] });
    }

    // Then save the visualisation
    const snapshot = getSnapshot(editor.store);
    saveVisualisation({
      caseId,
      chronologyId,
      snapshot,
    });
  };

  const handleDiscard = () => {
    if (!editor) return;
    if (originalSnapshot) {
      loadSnapshot(editor.store, {
        session: originalSnapshot.session,
        document: originalSnapshot.document,
      });
    } else {
      createTimeline(editor, chronology);
    }
    setTimeout(() => {
      discardAllChanges();
    }, 500);
  };

  return (
    <div className="flex items-center gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className={`flex cursor-pointer rounded-md border p-2 shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              hasUnsavedChanges
                ? 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100'
                : 'border-gray-100 hover:bg-gray-100'
            }`}
            onClick={handleSave}
            disabled={isSaving}
          >
            <Save className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent>Save changes</TooltipContent>
      </Tooltip>
      {/* Discard changes button (only show when there are unsaved changes) */}
      {hasUnsavedChanges && !isSaving && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="animate-fade-in flex cursor-pointer rounded-md border border-red-200 bg-red-50 p-2 text-red-700 shadow-sm transition-colors hover:bg-red-100"
              onClick={handleDiscard}
              role="button"
              tabIndex={0}
            >
              <X className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Discard unsaved changes</TooltipContent>
        </Tooltip>
      )}
      {/* Unsaved changes indicator */}
      {hasUnsavedChanges && !isSaving && (
        <div className="animate-pop-in absolute top-16 right-0 flex w-max items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-700 shadow-sm">
          <AlertTriangle className="h-3 w-3" />
          <span>{changeCount} unsaved</span>
        </div>
      )}
    </div>
  );
};
